import { translate } from '../../i18n/index.js';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { Spinner } from '../design/AtomFitUI.js';

interface InfiniteScrollProps {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  loadingLabel?: string;
  endLabel?: string;
  rootMargin?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function InfiniteScroll({
  hasMore,
  isLoading,
  onLoadMore,
  loadingLabel = translate('components.common.infinite_scroll.infinite_scroll.loading_label'),
  endLabel,
  rootMargin = '320px',
  className,
  style,
}: InfiniteScrollProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || isLoading) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) onLoadMore();
      },
      { rootMargin },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, onLoadMore, rootMargin]);

  if (!hasMore && !isLoading && !endLabel) {
    return <div ref={sentinelRef} aria-hidden="true" />;
  }

  return (
    <div
      ref={sentinelRef}
      className={className}
      style={{
        minHeight: 52,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        color: 'var(--af-muted)',
        fontSize: 13,
        ...style,
      }}
      aria-live="polite"
    >
      {isLoading ? (
        <>
          <Spinner />
          {loadingLabel}
        </>
      ) : hasMore ? null : (
        endLabel
      )}
    </div>
  );
}
